import { Fragment, useEffect, useState } from "react";
import { DateRange, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css"; // Estilos base para el DateRangePicker
import "react-date-range/dist/theme/default.css"; // Tema por defecto
import es from "date-fns/locale/es";

type DateRangePickerProps = {
  onDateChange?: (startDate: Date, endDate: Date) => void;
  id: string;
  label: string;
  value: { startDate: Date; endDate: Date };
  disabled?: boolean;
  span?: string;
  minDate?: Date;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateChange,
  id,
  label,
  value,
  disabled,
  span,
  minDate,
}) => {
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  useEffect(() => {
    if (value) {
      setState([
        {
          startDate: value.startDate,
          endDate: value.endDate,
          key: "selection",
        },
      ]);
    }
  }, [value]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("es-ES").replace(/\//g, "-");
  };

  const handleSelect = (ranges: RangeKeyDict) => {
    const { selection } = ranges;

    const startDate = selection.startDate || new Date();
    const endDate = selection.endDate || new Date();

    setState([
      {
        startDate,
        endDate,
        key: "selection",
      },
    ]);

    if (startDate && endDate && startDate !== endDate) {
      if (onDateChange) {
        onDateChange(startDate, endDate);
      }

      const datePicker = document.getElementById("date-range-picker");
      if (datePicker) {
        datePicker.classList.add("hidden");
      }
    }
  };

  const [isAbove, setIsAbove] = useState(false);
  const toggleDatePicker = () => {
    const datePicker = document.getElementById("date-range-picker");
    const inputField = document.getElementById(id);

    if (datePicker && inputField) {
      const rect = inputField.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Si no hay suficiente espacio debajo, despliega hacia arriba
      if (windowHeight - rect.bottom < 300) {
        setIsAbove(true);
      } else {
        setIsAbove(false);
      }

      datePicker.classList.toggle("hidden");
    }
  };

  return (
    <Fragment>
      <div className="relative">
        <div className="flex justify-between">
          <label
            htmlFor={id}
            className="block text-sm font-medium leading-6 text-gray-600"
          >
            {label}
          </label>
          <span
            aria-describedby={id}
            className={`text-sm leading-6 text-gray-500`}
          >
            {span}
          </span>
        </div>
        <input
          id={id}
          type="text"
          value={`${formatDate(state[0].startDate)} - ${formatDate(
            state[0].endDate
          )}`}
          readOnly
          disabled={disabled}
          className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:text-sm sm:leading-6"
          onClick={toggleDatePicker} // Al hacer clic, calculamos hacia dónde desplegar el calendario
          placeholder="Select date range"
        />

        <div
          id="date-range-picker"
          className={`align-center items-center absolute z-50 hidden mt-2 bg-white border border-gray-300 rounded-lg shadow-lg ${
            isAbove ? "bottom-full mb-2" : "top-full mt-2"
          }`} // Usamos bottom-full si no hay espacio debajo
        >
          <DateRange
            editableDateInputs={true}
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            ranges={state}
            className="date-range-picker"
            locale={es}
            minDate={minDate}
          />
        </div>
      </div>
    </Fragment>
  );
};
